/**
 * React Query hook for fetching proposals (carrier responses) for an RFP.
 *
 * Calls BTX endpoint: GET /api/btx/v1/rfps/{rfpId}/proposals?include=coverages
 */

import {
  keepPreviousData,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi } from "@platform/api";
import { useCallback } from "react";

import type { AxiosInstance } from "axios";
import type { CoverageTypeEnum } from "@platform/types";
import type {
  Proposal,
  ProposalCoverage,
  ProposalPlan,
  ProposalPlanGroup,
} from "../types/proposal.types";

// ---------------------------------------------------------------------------
// API response types — the Go backend serialises coverageType as "type", and
// the plan-level rate cap as "rateCap" (singular, matching the OpenAPI/canonical
// field name); the FE domain model uses "rateCap" throughout.
// ---------------------------------------------------------------------------

/** Raw coverage shape as returned by the API (uses `type`, not `coverageType`). */
interface RawProposalCoverage extends Omit<
  ProposalCoverage,
  "coverageType" | "planGroups" | "requestedProductPlanTypes"
> {
  type: CoverageTypeEnum;
  planGroups: RawProposalPlanGroup[];
  // Absent on a proposal predating this field.
  requestedProductPlanTypes?: ProposalCoverage["requestedProductPlanTypes"];
}

/** Raw plan group shape as returned by the API. */
interface RawProposalPlanGroup extends Omit<ProposalPlanGroup, "plans"> {
  plans: RawProposalPlan[];
}

/** Raw plan shape as returned by the API (uses `rateCap`, not `rateCap`). */
interface RawProposalPlan extends Omit<ProposalPlan, "rateCap"> {
  rateCap?: ProposalPlan["rateCap"];
}

/** Raw proposal shape as returned by the API. */
interface RawProposal extends Omit<Proposal, "coverages"> {
  coverages: RawProposalCoverage[];
}

/** Map API field names to the TypeScript domain model. */
const transformProposal = (raw: RawProposal): Proposal => ({
  ...raw,
  coverages: (raw.coverages ?? []).map(
    ({ type: coverageType, planGroups, ...rest }): ProposalCoverage => ({
      ...rest,
      coverageType,
      // A proposal predating this field, or the API omitting it, arrives as `undefined`
      // over the wire despite the domain type's `string[] | null` contract — normalize it
      // to `null` here so every downstream consumer can rely on that contract.
      requestedProductPlanTypes: rest.requestedProductPlanTypes ?? null,
      planGroups: (planGroups ?? []).map(
        ({ plans, ...groupRest }): ProposalPlanGroup => ({
          ...groupRest,
          plans: (plans ?? []).map(
            ({ rateCap, ...planRest }): ProposalPlan => ({
              ...planRest,
              rateCap,
            }),
          ),
        }),
      ),
    }),
  ),
});

// ---------------------------------------------------------------------------
// Query key
// ---------------------------------------------------------------------------

/**
 * Read scope for GET /proposals (ADR-031): `deliverable` = active proposals
 * only (no pending requotes or history — the pre-quote-history behaviour used
 * when the flag is off), `board` = active + un-actioned pending requotes,
 * `all` = every version.
 */
export type ProposalScope = "deliverable" | "board" | "all";

export const proposalQueryKeys = {
  all: (rfpId: string) => ["proposals", rfpId] as const,
  scoped: (rfpId: string, scope: ProposalScope) =>
    ["proposals", rfpId, scope] as const,
};

// ---------------------------------------------------------------------------
// Client function
// ---------------------------------------------------------------------------

const fetchProposals = async (
  rfpId: string,
  axiosInstance: AxiosInstance,
  scope: ProposalScope,
): Promise<Proposal[]> => {
  const params = new URLSearchParams({ include: "coverages" });
  // `deliverable` is the backend's default scope, requested by omitting the
  // param — the endpoint rejects any explicit value other than board/all.
  if (scope !== "deliverable") {
    params.set("scope", scope);
  }
  const response = await axiosInstance.get<RawProposal[]>(
    `btx/v1/rfps/${rfpId}/proposals?${params.toString()}`,
  );
  return response.data.map(transformProposal);
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Fetches proposals (with full coverage detail) for the given RFP.
 *
 * Takes its client from `useApi`, which the app provides at the root, so callers on either
 * side of PlanDesignerProvider work without threading an axios instance through.
 *
 * `scope` selects the ADR-031 read scope: `deliverable` returns active
 * proposals only (no requotes/history); `board` (default) adds un-actioned
 * pending requotes; `all` also returns actioned history. The query key
 * includes the scope so each scope caches independently.
 */
export const useProposals = (
  rfpId: string,
  options?: Omit<UseQueryOptions<Proposal[], Error>, "queryKey" | "queryFn">,
  scope: ProposalScope = "board",
): UseQueryResult<Proposal[], Error> => {
  const axiosInstance = useApi();

  return useQuery({
    queryKey: proposalQueryKeys.scoped(rfpId, scope),
    queryFn: () => fetchProposals(rfpId, axiosInstance, scope),
    placeholderData: keepPreviousData,
    ...options,
    enabled: !!rfpId && (options?.enabled ?? true),
  });
};

/**
 * How long a warmed proposals scope counts as fresh.
 *
 * Pass it to the reading `useProposals` as well as the prefetch, or the two disagree:
 * `staleTime` is per observer, not stored on the cache entry, so a reader left on the
 * default treats the warmed entry as stale the moment it mounts and refetches in the
 * background — the analyst sees no spinner, but every open costs a request anyway.
 *
 * Safe against acting on a stale list: Replace/Disregard/Restore invalidate
 * `proposalQueryKeys.all(rfpId)`, and invalidation marks an entry stale whatever its
 * staleTime, so the next read refetches regardless of this window.
 */
export const PROPOSALS_WARM_STALE_TIME_MS = 30_000;

/**
 * Returns a callback that warms the cache for a proposals scope, for a control that
 * reveals data it does not itself render — hovering the Quote History button, whose
 * dialog then queries the `all` scope.
 *
 * Keyed and fetched through the same helpers as {@link useProposals} on purpose: a
 * hand-built key would miss the entry the dialog reads and the prefetch would silently
 * do nothing. Safe to call repeatedly — React Query skips a fetch while the entry is
 * fresh, and returns immediately when one is already in flight.
 *
 * Takes its client from `useApi`, which the app provides at the root, so callers on either
 * side of PlanDesignerProvider can use this without threading an axios instance through.
 */
export const usePrefetchProposals = (
  rfpId: string,
  scope: ProposalScope = "board",
): (() => void) => {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();

  return useCallback(() => {
    if (!rfpId) return;

    void queryClient.prefetchQuery({
      queryKey: proposalQueryKeys.scoped(rfpId, scope),
      queryFn: () => fetchProposals(rfpId, axiosInstance, scope),
      staleTime: PROPOSALS_WARM_STALE_TIME_MS,
    });
  }, [queryClient, rfpId, axiosInstance, scope]);
};
